import type { ServiceTypeEnum } from "../../../Enums/FirmEnum";
import { ServiceType as FirmServiceType } from "../../../Enums/FirmEnum";
import type { ServiceTypeEnum as OfficeServiceTypeEnum } from "../../../Enums/OfficeEnum";
import type { IOfficeNew } from "../../../interfaces/models/IOffice";
import type { IAddress } from "../../../interfaces/models/IAddress";
import { getCurrentTimestamp } from "../../../utils/functions";
import { buildJurisdiction } from "../../../utils/jurisdiction/structuredJurisdiction";
import type { JurisdictionScope } from "../../../utils/jurisdiction/structuredJurisdiction";
import {
  parseOptionalProfessionalCount,
  toFirmProfessionalCountFields,
  toOfficeProfessionalCountFields,
} from "../../../utils/firmProfessionalCounts";
import type { CreateFirmFormState } from "./createFirmFormTypes";

const FIRM_SERVICE_TO_OFFICE: Record<
  ServiceTypeEnum,
  OfficeServiceTypeEnum
> = {
  Legal: "legal",
  Accounting: "accounting",
  "Investment Banking": "investmentBanking",
  Other: "other",
};

export function mapFirmServiceTypeToOffice(
  serviceType: ServiceTypeEnum,
): OfficeServiceTypeEnum {
  return FIRM_SERVICE_TO_OFFICE[serviceType];
}

function resolveLogoValue(logo: string | File | null): string {
  return typeof logo === "string" ? logo : "";
}

function normalizeEmailDomains(domains: string[]): string[] {
  return domains.map((domain) => domain.trim()).filter(Boolean);
}

export function buildCreateFirmJurisdiction(
  form: CreateFirmFormState,
  scope: JurisdictionScope,
) {
  return buildJurisdiction(
    {
      countryName: form.country.name,
      stateName: form.state,
      stateAbbreviation: form.stateAbbreviation,
      city: form.city,
    },
    scope,
  );
}

export function buildFirmPayload(
  form: CreateFirmFormState,
  regionNames: string,
) {
  const firmName = form.firmName.trim();
  const displayName = form.displayName.trim() || firmName;
  const jurisdiction = buildCreateFirmJurisdiction(form, "firm") ?? undefined;
  const stateName = form.state.trim();
  const cityName = form.city.trim();
  const yearFounded = form.yearFounded.trim();
  const professionalCounts = toFirmProfessionalCountFields({
    totalProfessionals: form.totalProfessionals,
    professionalsInPrimary: form.professionalsInPrimary,
  });

  return {
    isMember: true,
    name: displayName,
    firmName,
    serviceType: form.serviceType,
    membershipStatus: "Onboarding" as const,
    membershipType: "Primary" as const,
    country: form.country.id,
    countryName: form.country.name,
    state: stateName,
    stateName,
    cityId: cityName,
    cityName,
    jurisdiction,
    regionNames,
    region: regionNames ? [regionNames] : [],
    dateCreated: getCurrentTimestamp(),
    logo: resolveLogoValue(form.logo),
    smallLogo: "",
    phoneNumber: form.phoneNumber,
    websiteUrl: form.websiteUrl.trim(),
    yearFounded: yearFounded || undefined,
    acceptedEmailDomain: normalizeEmailDomains(form.acceptedEmailDomain),
    firmOverview: form.firmOverview.trim(),
    listingCategory: [],
    hasSecondary: false,
    notables: [],
    moreInformation: "",
    litigationPercentage: "",
    dateWithdrawn: undefined,
    // Legacy Loc_Save_Newmember: TypeListing A, no wsgFinancial until Member_Modify M/A.
    ...professionalCounts,
  };
}

export function buildAddressPayload(
  form: CreateFirmFormState,
  regionNames: string,
): Omit<IAddress, "id"> {
  const stateName = form.state.trim();
  const cityName = form.city.trim();

  return {
    address1: form.address1.trim(),
    address2: form.address2.trim(),
    zipCode: form.zipCode.trim(),
    noZipCode: !form.zipCode.trim(),
    addressType: "Office",
    city: cityName,
    cityName,
    state: stateName,
    stateName,
    country: form.country.id,
    countryName: form.country.name,
    regionNames,
    regions: [],
    venueLink: "",
    notes: "",
    altitude: 0,
    latitude: 0,
    createdDate: String(getCurrentTimestamp()),
  };
}

export function buildOfficePayload(
  form: CreateFirmFormState,
  firmId: string,
  firmName: string,
  addressId: string,
  regionName: string,
  serviceType: ServiceTypeEnum,
): Omit<IOfficeNew, "id"> {
  const jurisdiction = buildCreateFirmJurisdiction(form, "office") ?? "";
  const officeServiceType = mapFirmServiceTypeToOffice(serviceType);
  const stateName = form.state.trim();
  const cityName = form.city.trim();
  const officeProfessionalCounts = toOfficeProfessionalCountFields(
    parseOptionalProfessionalCount(form.professionalsInPrimary),
  );

  return {
    name: firmName,
    firm: firmId,
    firmName,
    officeType: "main",
    membershipType: "primary",
    membershipStatus: "Onboarding",
    serviceType: officeServiceType,
    showInDirectory: true,
    address: addressId,
    city: cityName,
    cityName,
    state: stateName,
    stateName,
    country: form.country.id,
    countryName: form.country.name,
    regionName,
    regionNames: regionName,
    jurisdiction,
    createdDate: getCurrentTimestamp(),
    ...officeProfessionalCounts,
  };
}

export function getServiceTypeOptions() {
  return Object.entries(FirmServiceType).map(([key, label]) => ({
    id: key,
    name: String(label),
  }));
}
